/*
  assets/cart-fixes.js  —  V6.9 cart hardening (2026-08-06)

  Loaded on EVERY page, after cart-notification.js / cart.js / custom.js.
  Everything here is additive: it wraps or delegates, it never redefines a theme class,
  so removing this one <script> tag reverts the theme to its previous behaviour.

  Fixes, keyed to the stress-test report:

  #1  Rapid +/- on the free-gift line could split it into a SECOND, fully charged line.
      Root cause: nothing serialised cart writes, so the gift app's re-add interleaved
      with an in-flight /cart/change. Here every cart mutation goes through one promise
      chain, so writes can no longer overlap.

  #2  Cart errors were rendered into a display:none element and never seen — a failed
      add-to-cart looked like a dead button. Here the real Shopify message
      ("Only 1 item was added to your cart due to availability.") is read off the
      response body and shown in a toast that works with the drawer open or closed.

  #4  Qualifying for the free gift silently blocked every discount code, with no way
      to decline the gift. [data-gift-decline] now removes it and remembers the choice
      for the session (with an Undo).

  #6  The header cart icon needed two clicks: custom.js bound it directly inside
      $(document).ready, so clicks before that were lost, and that path also bypassed
      the custom element's open() (no focus trap, no outside-click close, no analytics).
      Here it is a document-level delegated handler that calls the real open().

  #7  Pressing + at the 20-per-line cap did nothing and said nothing (stepUp() clamps
      silently, so no change event fired). Now it explains itself.
*/
(function () {
  'use strict';

  var GIFT_DECLINED_KEY = 'sfa_gift_declined';
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var GENERIC_ERROR = 'Something went wrong updating your cart — please try again.';
  var MUTATION_TIMEOUT_MS = 12000;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  /* ------------------------------------------------------------------ *
   * #2  Visible errors
   * ------------------------------------------------------------------ */

  var toastEl = null;
  var toastTimer = null;
  var TOAST_KEY = 'sfa_cart_toast';

  // Some failure paths in the theme end in a full page reload (the out-of-stock "+" is one),
  // which would wipe the message off the screen before the shopper could read it — the exact
  // silent-failure this fix exists to remove. So an error message is parked in sessionStorage
  // and replayed after a reload. It is cleared again 1.8s after being shown, so a message the
  // shopper has already read is never repeated on their next navigation.
  function persistToast(message) {
    try {
      sessionStorage.setItem(TOAST_KEY, JSON.stringify({ m: message, t: Date.now() }));
    } catch (e) {
      /* private mode */
    }
  }

  function clearPersistedToast() {
    try {
      sessionStorage.removeItem(TOAST_KEY);
    } catch (e) {}
  }

  function replayPersistedToast() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(TOAST_KEY);
      sessionStorage.removeItem(TOAST_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    var parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (parsed && parsed.m && Date.now() - parsed.t < 15000) toast(parsed.m);
  }

  function toast(message) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'sfa-cart-toast';
      toastEl.setAttribute('role', 'alert');
      toastEl.setAttribute('aria-live', 'assertive');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    // Visibility is driven by INLINE styles, not by the .is-visible class. This is the
    // one path that has to work no matter what — the whole point of the fix is that the
    // shopper sees the message — and inline styles can't be lost to a cascade or
    // specificity surprise from the theme's or an app's CSS. cart-fixes.css still
    // supplies the appearance; the class is kept for styling hooks only.
    toastEl.classList.add('is-visible');
    void toastEl.offsetWidth; // replay the transition on a repeat message
    toastEl.style.setProperty('opacity', '1', 'important');
    toastEl.style.setProperty('visibility', 'visible', 'important');
    toastEl.style.setProperty('transform', 'translate(-50%, 0)', 'important');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
      toastEl.style.setProperty('opacity', '0', 'important');
      toastEl.style.setProperty('visibility', 'hidden', 'important');
      toastEl.style.setProperty('transform', 'translate(-50%, 12px)', 'important');
      // The message has now been on screen for its full run, so drop the stored copy.
      // If the page reloads BEFORE this fires — which is exactly what the failing
      // out-of-stock path does — the copy survives and is replayed on the next load.
      clearPersistedToast();
    }, 5000);
  }

  window.sfaCartToast = toast;

  // Last real message Shopify gave us, so the drawer's generic banner can be upgraded.
  var lastServerError = null;

  function readServerError(response) {
    // Clone: the theme still wants to read the body itself.
    var clone;
    try {
      clone = response.clone();
    } catch (e) {
      return Promise.resolve(null);
    }
    return clone
      .text()
      .then(function (text) {
        var parsed = null;
        try {
          parsed = JSON.parse(text);
        } catch (e) {
          return null;
        }
        if (!parsed) return null;
        return parsed.description || parsed.message || null;
      })
      .catch(function () {
        return null;
      });
  }

  /* ------------------------------------------------------------------ *
   * #1  Serialise every cart mutation
   * ------------------------------------------------------------------ */

  var chain = Promise.resolve();

  function enqueue(task) {
    // Runs task exactly once, only after everything already queued has settled.
    // The caller gets the real promise (including rejections); the chain itself never
    // rejects and is released by a timeout so one hung request can't wedge the cart.
    var result = chain.then(task, task);
    chain = new Promise(function (release) {
      var timer = setTimeout(release, MUTATION_TIMEOUT_MS);
      var free = function () {
        clearTimeout(timer);
        release();
      };
      result.then(free, free);
    });
    return result;
  }

  var nativeFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    var url = '';
    try {
      url = typeof input === 'string' ? input : input && input.url ? input.url : '';
    } catch (e) {
      url = '';
    }

    if (!url || !CART_MUTATION_RE.test(url)) {
      return nativeFetch(input, init);
    }

    return enqueue(function () {
      return nativeFetch(input, init).then(function (response) {
        if (!response.ok) {
          return readServerError(response).then(function (message) {
            if (message) {
              lastServerError = message;
              persistToast(message);
              toast(message);
            }
            return response;
          });
        }
        // A 200 is not the same as "we did what you asked". /cart/change.js silently
        // CLAMPS an over-stock quantity and returns 200 with the old number, which is
        // why pressing + on the last unit in stock looked like a dead button. Compare
        // what we asked for against what came back and say so.
        return reportClamp(url, init, response).then(function () {
          return response;
        });
      });
    });
  };

  function reportClamp(url, init, response) {
    if (!/\/cart\/(change|update)(\.js)?/.test(url) || !init || typeof init.body !== 'string') {
      return Promise.resolve();
    }
    var requested = null;
    try {
      requested = JSON.parse(init.body);
    } catch (e) {
      return Promise.resolve();
    }
    if (!requested) return Promise.resolve();

    var clone;
    try {
      clone = response.clone();
    } catch (e) {
      return Promise.resolve();
    }

    return clone
      .json()
      .then(function (cart) {
        if (!cart || !cart.items) return;

        function say(item, want) {
          if (!item || typeof want !== 'number' || item.quantity >= want) return;
          var left = item.quantity;
          var message =
            left > 0
              ? 'Only ' + left + ' left in stock — quantity set to ' + left + '.'
              : 'Sorry, that item is out of stock.';
          // Also park it as the "real reason": the theme notices the mismatch a moment
          // later and calls showError() with its generic string, which would otherwise
          // overwrite this more useful message.
          lastServerError = message;
          persistToast(message);
          toast(message);
        }

        if (requested.updates && typeof requested.updates === 'object') {
          Object.keys(requested.updates).forEach(function (key) {
            var want = parseInt(requested.updates[key], 10);
            var item = cart.items.filter(function (i) {
              return i.key === key;
            })[0];
            say(item, want);
          });
          return;
        }

        var wanted = parseInt(requested.quantity, 10);
        if (isNaN(wanted)) return;
        var line = null;
        if (requested.id) {
          line = cart.items.filter(function (i) {
            return i.key === requested.id || String(i.variant_id) === String(requested.id);
          })[0];
        } else if (requested.line) {
          line = cart.items[parseInt(requested.line, 10) - 1];
        }
        say(line, wanted);
      })
      .catch(function () {
        /* not JSON, or the shape changed — stay quiet rather than guess */
      });
  }

  /* ------------------------------------------------------------------ *
   * #2 (cont.)  Upgrade the drawer's generic banner to the real reason
   * ------------------------------------------------------------------ */

  function patchDrawerErrors() {
    var ctor = window.customElements && customElements.get('cart-notification');
    if (!ctor || !ctor.prototype || ctor.prototype.__sfaPatched) return;
    var proto = ctor.prototype;
    var originalShowError = proto.showError;

    proto.showError = function (message) {
      var text = message;
      // The theme falls back to a generic string on any failed change; if we captured
      // the real reason from the response body moments ago, show that instead.
      if ((!text || text === GENERIC_ERROR) && lastServerError) {
        text = lastServerError;
      }
      lastServerError = null;
      persistToast(text || GENERIC_ERROR);
      toast(text || GENERIC_ERROR);
      if (typeof originalShowError === 'function') {
        try {
          originalShowError.call(this, text);
        } catch (e) {
          /* the toast already carried the message */
        }
      }
    };

    // Precise stock message. /cart/change.js does NOT fail on an over-stock quantity —
    // it returns 200 with the number silently clamped — so the only way to know is to
    // compare what was asked for against what the cart actually holds afterwards.
    // Done here rather than in the fetch wrapper because at least one app on the
    // storefront replaces window.fetch with its own reference, which can bypass ours;
    // a prototype patch is resolved at call time and can't be sidestepped that way.
    var originalUpdateQuantity = proto.updateQuantity;
    if (typeof originalUpdateQuantity === 'function') {
      proto.updateQuantity = function (line, quantity, name, itemEl) {
        var want = parseInt(quantity, 10);
        var key = itemEl && itemEl.dataset ? itemEl.dataset.key : null;
        if (!key) {
          var input = this.querySelector('input[data-index="' + line + '"]');
          var host = input && input.closest ? input.closest('[data-key]') : null;
          if (host && host.dataset) key = host.dataset.key;
        }
        var out = originalUpdateQuantity.apply(this, arguments);
        if (!isNaN(want) && want > 0) {
          setTimeout(function () {
            getCart()
              .then(function (cart) {
                var items = cart.items || [];
                var item = null;
                if (key) {
                  item = items.filter(function (i) {
                    return i.key === key;
                  })[0];
                }
                if (!item && line) item = items[parseInt(line, 10) - 1];
                if (!item || item.quantity >= want) return;
                var left = item.quantity;
                var message =
                  left > 0
                    ? 'Only ' + left + ' left in stock — quantity set to ' + left + '.'
                    : 'Sorry, that item is out of stock.';
                lastServerError = message;
                persistToast(message);
                toast(message);
              })
              .catch(function () {});
          }, 1400);
        }
        return out;
      };
    }

    proto.__sfaPatched = true;
  }

  /* ------------------------------------------------------------------ *
   * #2 (cont.)  Catch every inline error the theme writes but hides
   * ------------------------------------------------------------------ */

  // The theme writes real messages into elements that are display:none, or that live
  // inside a container the shopper isn't looking at. Rather than chase each one, watch
  // for any of them gaining text and mirror it into the toast.
  var INLINE_ERROR_SELECTOR = '.product-form__error-message, .cart-item__error-text, [data-cart-error]';
  var seenErrors = {};

  function checkInlineError(node) {
    if (!node || node.nodeType !== 1) return;
    var els = [];
    if (node.matches && node.matches(INLINE_ERROR_SELECTOR)) els.push(node);
    if (node.querySelectorAll) {
      els = els.concat([].slice.call(node.querySelectorAll(INLINE_ERROR_SELECTOR)));
    }
    els.forEach(function (el) {
      var text = (el.textContent || '').trim();
      if (!text || text === GENERIC_ERROR) return;
      // De-dupe: the same message can be written to several elements at once.
      if (seenErrors[text] && Date.now() - seenErrors[text] < 4000) return;
      seenErrors[text] = Date.now();
      lastServerError = text;
      persistToast(text);
      toast(text);
    });
  }

  function watchInlineErrors() {
    if (!window.MutationObserver) return;
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        if (record.type === 'characterData') {
          checkInlineError(record.target.parentElement);
          return;
        }
        checkInlineError(record.target);
        [].slice.call(record.addedNodes).forEach(checkInlineError);
      });
    }).observe(document.documentElement, { subtree: true, childList: true, characterData: true });
  }

  /* ------------------------------------------------------------------ *
   * #6  Header cart icon opens the drawer on the first click
   * ------------------------------------------------------------------ */

  function drawerEl() {
    return document.querySelector('cart-notification');
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var opener = target.closest('[data-action="open-drawer"][data-drawer-id="cart-notification"]');
      if (opener) {
        if (document.body.classList.contains('template-cart')) return; // cart page has no drawer
        event.preventDefault();
        var drawer = drawerEl();
        if (drawer && typeof drawer.open === 'function') {
          // Real open(): focus trap, outside-click close, cart:open event.
          // Deferred by one tick on purpose: open() attaches its own
          // document.body click listener to close on an outside click, and if we call
          // it synchronously that listener catches THIS click as it finishes bubbling
          // and shuts the drawer again immediately — which looks exactly like the
          // first-click-does-nothing bug we're fixing.
          setTimeout(function () {
            drawer.open('header');
          }, 0);
        } else {
          // Drawer script not ready yet — fall back to the class toggle so the very
          // first click is never swallowed, exactly as custom.js would have done.
          var el = document.getElementById('cart-notification');
          if (el) {
            el.classList.add('active');
            var overlay = document.querySelector('.PageOverlay');
            if (overlay) overlay.classList.add('is-visible');
            document.body.classList.add('overflow-hidden');
          }
        }
        return;
      }

      var closer = target.closest('[data-action="close-drawer"][data-drawer-id="cart-notification"]');
      if (closer) {
        var d = drawerEl();
        // The close button lives inside an AJAX-swapped section, so the constructor's
        // direct binding is destroyed on every cart update. Route it back to close().
        if (d && typeof d.close === 'function') {
          event.preventDefault();
          d.close();
        }
      }
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * #7  Quantity cap says so
   * ------------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest ? event.target.closest('button[name="plus"]') : null;
    if (!btn) return;
    var wrap = btn.closest('quantity-input');
    if (!wrap) return;
    var input = wrap.querySelector('input[type="number"]');
    if (!input) return;
    var max = parseInt(input.getAttribute('max'), 10);
    if (isNaN(max)) return;
    if (parseInt(input.value, 10) >= max) {
      toast('Maximum ' + max + ' per item. Contact us if you need a larger order.');
    }
  });

  /* ------------------------------------------------------------------ *
   * #4  Decline the free gift so a discount code can be used instead
   * ------------------------------------------------------------------ */

  function giftDeclined() {
    try {
      return sessionStorage.getItem(GIFT_DECLINED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setGiftDeclined(value) {
    try {
      if (value) sessionStorage.setItem(GIFT_DECLINED_KEY, '1');
      else sessionStorage.removeItem(GIFT_DECLINED_KEY);
    } catch (e) {
      /* private mode — the decline just won't persist across reloads */
    }
    document.documentElement.classList.toggle('sfa-gift-declined', !!value);
  }

  function isGiftLine(item) {
    if (!item) return false;
    if (item.properties && item.properties._Gifted) return true;
    if (item.url && item.url.indexOf('sca_clone_freegift') !== -1) return true;
    return item.original_line_price > 0 && item.final_line_price === 0;
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function removeGiftLines() {
    return getCart().then(function (cart) {
      var gift = (cart.items || []).filter(isGiftLine);
      if (!gift.length) return false;
      var updates = {};
      gift.forEach(function (item) {
        updates[item.key] = 0;
      });
      return postJSON(root() + 'cart/update.js', { updates: updates }).then(function () {
        return true;
      });
    });
  }

  function refreshCartUI() {
    var drawer = drawerEl();
    if (drawer && typeof drawer.refreshCartNotification === 'function') {
      try {
        drawer.refreshCartNotification();
        return;
      } catch (e) {
        /* fall through to reload */
      }
    }
    window.location.reload();
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var decline = target.closest('[data-gift-decline]');
    if (decline) {
      event.preventDefault();
      decline.setAttribute('disabled', '');
      setGiftDeclined(true);
      removeGiftLines()
        .then(function () {
          toast('Gift removed — you can use a discount code now.');
          showUndoNotice();
          refreshCartUI();
        })
        .catch(function () {
          decline.removeAttribute('disabled');
        });
      return;
    }

    var undo = target.closest('[data-gift-undo]');
    if (undo) {
      event.preventDefault();
      undo.setAttribute('disabled', '');
      setGiftDeclined(false);
      hideUndoNotice();
      nudgeCart().then(refreshCartUI, refreshCartUI);
    }
  });

  // The gift app only re-evaluates on a real quantity DELTA. Verified against the live app:
  // an attributes-only write, and a change that sets a line to the quantity it already has,
  // are both ignored. So to bring a declined gift back we bump a paid line by one and put it
  // straight back — two real deltas, ordered by the queue above.
  function nudgeCart() {
    return getCart().then(function (cart) {
      var paid = (cart.items || []).filter(function (item) {
        return !isGiftLine(item);
      })[0];
      if (!paid) return null;

      var qty = paid.quantity;

      function setQty(key, value) {
        var updates = {};
        updates[key] = value;
        return postJSON(root() + 'cart/update.js', { updates: updates });
      }

      // The line key can change when the app rewrites the cart, so re-read it before
      // restoring rather than reusing the stale one.
      function restore() {
        return getCart().then(function (fresh) {
          var line = (fresh.items || []).filter(function (item) {
            return !isGiftLine(item) && item.variant_id === paid.variant_id;
          })[0];
          return setQty(line ? line.key : paid.key, qty);
        });
      }

      return setQty(paid.key, qty + 1).then(function (response) {
        if (response && response.ok) return restore();
        // qty+1 refused (stock) — go the other way, but never down to zero.
        if (qty > 1) return setQty(paid.key, qty - 1).then(restore);
        return null;
      });
    });
  }

  var undoEl = null;

  function showUndoNotice() {
    if (!giftDeclined()) return;
    if (!undoEl) {
      undoEl = document.createElement('div');
      undoEl.className = 'sfa-gift-undo';
      undoEl.innerHTML =
        '<span>Free gift skipped for this order.</span> ' +
        '<button type="button" data-gift-undo class="sfa-gift-undo__btn">Add it back</button>';
    }
    var host =
      document.querySelector('#cart-notification-product') ||
      document.querySelector('.cart__items .js-contents') ||
      null;
    if (host && undoEl.parentNode !== host) host.insertBefore(undoEl, host.firstChild);
  }

  function hideUndoNotice() {
    if (undoEl && undoEl.parentNode) undoEl.parentNode.removeChild(undoEl);
  }

  // If the gift app re-adds a declined gift on the next cart write, take it back out.
  function enforceDecline() {
    if (!giftDeclined()) return;
    getCart().then(function (cart) {
      if (!(cart.items || []).some(isGiftLine)) {
        showUndoNotice();
        return;
      }
      removeGiftLines().then(function (removed) {
        showUndoNotice();
        if (removed) refreshCartUI();
      });
    });
  }

  document.addEventListener('cart:updated', enforceDecline);
  document.addEventListener('cart:refresh', enforceDecline);

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */

  function boot() {
    patchDrawerErrors();
    watchInlineErrors();
    replayPersistedToast();
    setGiftDeclined(giftDeclined());
    if (giftDeclined()) {
      showUndoNotice();
      enforceDecline();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // cart-notification.js is deferred; make sure the prototype patch lands either way.
  window.addEventListener('load', patchDrawerErrors);
})();
